// engagement — fotixa (Uzbek pre-wedding promise) guest variant.
//
// Design notes (f01 Fotixa — Aziza & Sardor, 5 April 2026, Tashkent):
//   * The hero is intimate, not theatrical: ivory backdrop, a centered
//     rose-gold photo medallion, and the names in Pinyon Script as the
//     focal moment. On load, the script "draws itself" via an animated
//     clip-path inset — a soft 2.5s ease-out reveal, mirroring the
//     calligrapher's hand. Reduced-motion users see the static names.
//   * "OILAMIZ" eyebrow opens the story — not "our story". Body text
//     describes the fathers shaking hands and the mothers exchanging
//     gifts: the literal beats of a fotixa.
//   * Between Story and Gallery sits a UNIQUE family-tree section. Two
//     roots (kuyov tomon / kelin tomon) merge into one trunk and bloom
//     into a single canopy at the top — a literal joining of two families.
//     Branch names list the immediate family on each side.
//   * Schedule is tightened to a fotixa rhythm (15:00–19:00). Map points
//     at the bride's mahalla guzar; RSVP label asks the families to join.
//   * No music player and no guestbook — fotixa is a more solemn family
//     event than the wedding itself. Two-three subtle sound triggers:
//       - chime + tap on RSVP "yes" submit
//       - tap on share buttons
//       - tap on family-tree branch hover
//
// All photos resolve from t.photos. There are NO fallback strings — when a
// photo is absent we render an ivory-and-rose-gold placeholder gradient.
import { useEffect, useRef, useState } from "react";
import {
  GuestPhoto,
  CountdownSection,
  StorySection,
  GallerySection,
  ScheduleSection,
  MapSection,
  RsvpSection,
  ShareSection,
  GuestFooter,
} from "@/components/sections";
import { useSound } from "@/lib/sound";
import { useT } from "@/lib/i18n";
import * as Ornaments from "@/components/Ornaments";
import type { VariantProps } from "./types";

// ─── prefers-reduced-motion ────────────────────────────────────────────
function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduce(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReduce(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return reduce;
}

// Family tree data — left side groom, right side bride. The tree itself
// describes the joining of these two trunks into one canopy.
// Side identifiers; per-side labels and member lists live in the dictionary.
const FAMILY_SIDES: Array<{ side: "kuyov" | "kelin"; titleKey: string; memberPrefix: string }> = [
  { side: "kuyov", titleKey: "variant.f01.family.groom_label", memberPrefix: "variant.f01.family.groom" },
  { side: "kelin", titleKey: "variant.f01.family.bride_label", memberPrefix: "variant.f01.family.bride" },
];

// ═══════════════════════════════════════════════════════════════════════
// Main variant
// ═══════════════════════════════════════════════════════════════════════
export function EngagementVariant({ t, isDark, ctx }: VariantProps) {
  const tt = useT();
  const { play } = useSound();
  const reduce = usePrefersReducedMotion();
  const [namesDrawn, setNamesDrawn] = useState(false);

  // Trigger the "draw" animation just after mount so the keyframe runs
  // even on fast paints. Reduced-motion skips straight to the final state.
  useEffect(() => {
    if (reduce) {
      setNamesDrawn(true);
      return;
    }
    const id = window.requestAnimationFrame(() => setNamesDrawn(true));
    return () => window.cancelAnimationFrame(id);
  }, [reduce]);

  const photos = t.photos || [];
  const heroPhoto = photos[0];

  // RSVP wrapper: chime + tap on "yes", tap on "no".
  const wrappedSetRsvp = (v: string | null) => {
    if (v === "yes") {
      play("chime");
      window.setTimeout(() => play("tap"), 140);
    } else if (v === "no") {
      play("tap");
    }
    ctx.rsvp.setRsvp(v);
  };

  return (
    <div className="fotixa-variant">
      <style>{`
        /* The signature "draw" animation on hero names. Names start with
           clip-path: inset(0 100% 0 0) (fully clipped to the right) and
           ease out to inset(0 0 0 0). Soft 2.5s — like a brush stroke. */
        @keyframes fotixaDrawNames {
          0%   { clip-path: inset(0 100% 0 0); -webkit-clip-path: inset(0 100% 0 0); }
          100% { clip-path: inset(0 0 0 0);    -webkit-clip-path: inset(0 0 0 0); }
        }
        @keyframes fotixaFadeUp {
          0%   { opacity: 0; transform: translateY(12px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes fotixaPulseDot {
          0%, 100% { transform: scale(1); opacity: 0.85; }
          50%      { transform: scale(1.2); opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          .fotixa-names-draw,
          .fotixa-fade,
          .fotixa-pulse {
            animation: none !important;
            clip-path: inset(0 0 0 0) !important;
            -webkit-clip-path: inset(0 0 0 0) !important;
            opacity: 1 !important;
            transform: none !important;
          }
        }

        /* Mobile responsive trims — keep the medallion proportional and
           give the script names extra breathing room on narrow screens. */
        @media (max-width: 768px) {
          .fotixa-variant .fotixa-hero {
            padding: 64px 18px 48px !important;
            min-height: auto !important;
          }
          .fotixa-variant .fotixa-medallion {
            width: 220px !important;
            height: 220px !important;
            margin-bottom: 28px !important;
          }
          /* The cardinal-dot ornaments are positioned via transform:
             rotate(deg) translate(0, -172px) which assumed the 320px
             medallion radius. At 220px (radius=110px) the translate
             would push dots outside the medallion border. Hide them on
             mobile — they're purely decorative and invisible anyway
             behind the portrait. */
          .fotixa-variant .fotixa-medallion .fotixa-pulse {
            display: none !important;
          }
          .fotixa-variant .fotixa-names {
            font-size: clamp(36px, 12vw, 72px) !important;
            line-height: 1 !important;
          }
          .fotixa-variant .fotixa-tree-section {
            padding: 64px 18px !important;
          }
          .fotixa-variant .fotixa-tree-grid {
            grid-template-columns: 1fr !important;
            gap: 28px !important;
          }
          .fotixa-variant .fotixa-tree-svg {
            height: 220px !important;
          }
          .fotixa-variant button { min-height: 44px; }
          /* Ensure hero content is visible when mobile animation-kill
             removes the fotixaFadeUp keyframe that would set opacity: 1 */
          .fotixa-variant .fotixa-hero .fotixa-fade {
            opacity: 1 !important;
          }
        }
        .fotixa-variant button:focus-visible { outline: 2px solid ${t.accent}; outline-offset: 2px; }
      `}</style>

      {/* ═══════════════════════════════════════════════════════════════
          HERO — ivory backdrop, rose-gold medallion, Pinyon Script names
          ═══════════════════════════════════════════════════════════════ */}
      <section
        className="fotixa-hero"
        style={{
          minHeight: "100vh",
          padding: "96px 48px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          background: t.bg,
          color: t.ink,
          position: "relative",
        }}
      >
        {/* Eyebrow */}
        <div
          className="fotixa-fade"
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: t.accent,
            marginBottom: 32,
            animation: reduce ? "none" : "fotixaFadeUp 700ms ease both 200ms",
          }}
        >
          {tt("variant.f01.hero.eyebrow")}
        </div>

        {/* Rose-gold photo medallion */}
        <div
          className="fotixa-medallion"
          style={{
            width: 320,
            height: 320,
            position: "relative",
            marginBottom: 48,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #D4A5A5 0%, #E9CCB6 25%, #F4E2CE 50%, #E9CCB6 75%, #D4A5A5 100%)",
            padding: 8,
            boxShadow: `0 18px 40px ${t.accent}33, 0 4px 0 ${t.accent}22`,
            animation: reduce ? "none" : "fotixaFadeUp 800ms ease both 350ms",
          }}
        >
          <div
            style={{
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              background: t.bg,
              padding: 6,
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                overflow: "hidden",
                position: "relative",
              }}
            >
              <GuestPhoto
                src={heroPhoto}
                style={{ width: "100%", height: "100%" }}
                fallbackBg={t.bg}
                fallbackTone={t.accent}
              />
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  inset: 0,
                  background: `linear-gradient(180deg, transparent 60%, ${t.accent}22 100%)`,
                }}
              />
            </div>
          </div>
          {/* Four tiny accent dots at the cardinal points */}
          {[0, 90, 180, 270].map((deg) => (
            <span
              key={deg}
              aria-hidden
              className="fotixa-pulse"
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: t.accent,
                transform: `rotate(${deg}deg) translate(0, -172px)`,
                transformOrigin: "0 0",
                animation: reduce ? "none" : `fotixaPulseDot 3.2s ease-in-out ${(deg / 360) * 2}s infinite`,
              }}
            />
          ))}
        </div>

        {/* Names — accessible <h1> with a clip-path "ink stroke" reveal
              animation over 2.5s ease-out. Single element so screen
              readers announce the names exactly once. */}
        {/* maxWidth: 100% + overflowX hidden prevents the nowrap from
            causing page-level horizontal scroll on narrow phones. */}
        <div style={{ position: "relative", display: "inline-block", maxWidth: "100%", overflowX: "hidden" }}>
          <h1
            className="fotixa-names fotixa-names-draw"
            style={{
              fontFamily: t.font,
              fontSize: "clamp(72px, 11vw, 144px)",
              fontWeight: 400,
              lineHeight: 1.05,
              letterSpacing: "0.005em",
              color: t.ink,
              margin: 0,
              /* whiteSpace: nowrap removed — caused horizontal overflow on
                 ≤390px phones for names that don't fit in one line. The
                 clip-path draw animation works correctly without it. */
              clipPath: namesDrawn ? "inset(0 0 0 0)" : "inset(0 100% 0 0)",
              WebkitClipPath: namesDrawn ? "inset(0 0 0 0)" : "inset(0 100% 0 0)",
              transition: reduce
                ? "none"
                : "clip-path 2500ms cubic-bezier(0.22, 1, 0.36, 1), -webkit-clip-path 2500ms cubic-bezier(0.22, 1, 0.36, 1)",
            }}
          >
            {t.names}
          </h1>
        </div>

        {/* Date · venue */}
        <div
          className="fotixa-fade"
          style={{
            marginTop: 36,
            fontFamily: "var(--font-ui)",
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: "0.3em",
            color: `${t.ink}cc`,
            animation: reduce ? "none" : "fotixaFadeUp 800ms ease both 2200ms",
          }}
        >
          {t.date} · {t.venue.toUpperCase()}
        </div>

        {/* Decorative flourish */}
        <div
          className="fotixa-fade"
          style={{
            marginTop: 28,
            color: t.accent,
            animation: reduce ? "none" : "fotixaFadeUp 800ms ease both 2400ms",
          }}
        >
          <Ornaments.Flourish width={140} color={t.accent} />
        </div>
      </section>

      <CountdownSection t={t} {...ctx.countdown} isDark={isDark} />

      <StorySection
        t={t}
        eyebrow={tt("variant.f01.story.eyebrow")}
        title={tt("variant.f01.story.title")}
        body={tt("variant.f01.story.body")}
      />

      {/* ═══════════════════════════════════════════════════════════════
          UNIQUE: Family Tree — two roots merge into one canopy
          ═══════════════════════════════════════════════════════════════ */}
      <FamilyTreeSection t={t} onHoverBranch={() => play("tap")} />

      <GallerySection t={t} />

      <ScheduleSection
        t={t}
        items={[
          ["15:00", tt("variant.f01.schedule.0.event")],
          ["15:30", tt("variant.f01.schedule.1.event")],
          ["16:00", tt("variant.f01.schedule.2.event")],
          ["17:00", tt("variant.f01.schedule.3.event")],
          ["18:00", tt("variant.f01.schedule.4.event")],
          ["19:00", tt("variant.f01.schedule.5.event")],
        ]}
      />

      <MapSection
        t={t}
        isDark={isDark}
        addressLine={tt("variant.f01.map.address")}
      />

      <RsvpSection
        t={t}
        isDark={isDark}
        rsvp={ctx.rsvp.rsvp}
        setRsvp={wrappedSetRsvp}
        guests={ctx.rsvp.guests}
        setGuests={ctx.rsvp.setGuests}
        guestName={ctx.rsvp.guestName}
        setGuestName={ctx.rsvp.setGuestName}
        slug={ctx.slug}
        label={tt("variant.f01.rsvp.label")}
      />

      {/* Tap-feedback wrapper for share buttons. ShareSection itself doesn't
          accept a sound callback, but pointerdown bubbling lets us play a
          subtle tap on every share intent button. */}
      <div onPointerDown={() => play("tap")}>
        <ShareSection t={t} />
      </div>

      <GuestFooter t={t} />

    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// FamilyTreeSection — SVG with two roots merging into a single canopy.
// ═══════════════════════════════════════════════════════════════════════
function FamilyTreeSection({
  t,
  onHoverBranch,
}: {
  t: VariantProps["t"];
  onHoverBranch: () => void;
}) {
  const tt = useT();
  return (
    <section
      className="fotixa-tree-section"
      style={{
        padding: "120px 48px",
        borderTop: `1px solid ${t.ink}22`,
        background: `${t.accent}10`,
        textAlign: "center",
      }}
    >
      <div style={{ maxWidth: 880, margin: "0 auto" }}>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: t.accent,
            marginBottom: 16,
          }}
        >
          {tt("variant.f01.family.eyebrow")}
        </div>
        <h2
          style={{
            fontFamily: t.font || "var(--font-display)",
            fontSize: "clamp(36px, 5vw, 56px)",
            fontWeight: 400,
            marginBottom: 24,
            letterSpacing: "-0.005em",
          }}
        >
          {tt("variant.f01.family.title")}
        </h2>
        <p
          style={{
            fontFamily: "var(--font-body)",
            fontSize: 17,
            lineHeight: 1.7,
            fontStyle: "italic",
            color: `${t.ink}cc`,
            maxWidth: 560,
            margin: "0 auto 56px",
          }}
        >
          {tt("variant.f01.family.intro")}
        </p>

        {/* The tree itself — single SVG, two roots → one trunk → canopy. */}
        <div
          aria-hidden
          style={{
            width: "100%",
            maxWidth: 640,
            margin: "0 auto",
            background: t.bg,
            border: `1px solid ${t.accent}55`,
            padding: 24,
            boxShadow: `4px 5px 0 -1px ${t.accent}33`,
          }}
        >
          <svg
            className="fotixa-tree-svg"
            viewBox="0 0 400 320"
            style={{ width: "100%", height: 280, display: "block" }}
            preserveAspectRatio="xMidYMid meet"
          >
            <defs>
              <radialGradient id="fotixa-canopy-grad" cx="50%" cy="55%" r="55%">
                <stop offset="0%" stopColor={t.accent} stopOpacity="0.55" />
                <stop offset="60%" stopColor={t.accent} stopOpacity="0.28" />
                <stop offset="100%" stopColor={t.accent} stopOpacity="0.05" />
              </radialGradient>
              <linearGradient id="fotixa-trunk-grad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={t.accent} />
                <stop offset="100%" stopColor={`${t.ink}aa`} />
              </linearGradient>
            </defs>

            {/* Soft ground line */}
            <line
              x1="20"
              y1="298"
              x2="380"
              y2="298"
              stroke={`${t.ink}33`}
              strokeWidth="0.8"
              strokeDasharray="2 4"
            />

            {/* Two roots curving in to the centre at y=240 */}
            <path
              d="M 80 296 C 100 280 140 260 200 240"
              stroke="url(#fotixa-trunk-grad)"
              strokeWidth="3"
              fill="none"
              strokeLinecap="round"
            />
            <path
              d="M 320 296 C 300 280 260 260 200 240"
              stroke="url(#fotixa-trunk-grad)"
              strokeWidth="3"
              fill="none"
              strokeLinecap="round"
            />
            {/* Root labels */}
            <text
              x="80"
              y="312"
              textAnchor="middle"
              fontFamily="var(--font-ui)"
              fontSize="9"
              fontWeight="700"
              fill={t.accent}
              letterSpacing="0.18em"
            >
              KUYOV
            </text>
            <text
              x="320"
              y="312"
              textAnchor="middle"
              fontFamily="var(--font-ui)"
              fontSize="9"
              fontWeight="700"
              fill={t.accent}
              letterSpacing="0.18em"
            >
              KELIN
            </text>

            {/* Trunk — single, rises from the meeting point */}
            <path
              d="M 200 240 L 200 130"
              stroke="url(#fotixa-trunk-grad)"
              strokeWidth="6"
              fill="none"
              strokeLinecap="round"
            />

            {/* Canopy halo */}
            <ellipse
              cx="200"
              cy="100"
              rx="140"
              ry="80"
              fill="url(#fotixa-canopy-grad)"
            />

            {/* Branches inside the canopy */}
            {[
              { x1: 200, y1: 160, x2: 130, y2: 90 },
              { x1: 200, y1: 160, x2: 270, y2: 90 },
              { x1: 200, y1: 140, x2: 90, y2: 130 },
              { x1: 200, y1: 140, x2: 310, y2: 130 },
              { x1: 200, y1: 150, x2: 200, y2: 50 },
            ].map((b, i) => (
              <path
                key={i}
                d={`M ${b.x1} ${b.y1} Q ${(b.x1 + b.x2) / 2} ${(b.y1 + b.y2) / 2 - 10} ${b.x2} ${b.y2}`}
                stroke={t.accent}
                strokeWidth="1.5"
                fill="none"
                opacity="0.85"
              />
            ))}

            {/* Blossoms — one for each family member, arranged in the canopy */}
            {[
              { x: 130, y: 90 },
              { x: 270, y: 90 },
              { x: 90, y: 130 },
              { x: 310, y: 130 },
              { x: 200, y: 50 },
              { x: 175, y: 100 },
              { x: 225, y: 100 },
            ].map((p, i) => (
              <g key={i} transform={`translate(${p.x} ${p.y})`}>
                <circle r="6" fill={t.accent} opacity="0.85" />
                <circle r="3" fill={t.bg} />
                <circle r="1.5" fill={t.accent} />
              </g>
            ))}

            {/* Center heart at the trunk-root junction — the union point */}
            <g transform="translate(200 240)">
              <path
                d="M 0 4 L -7 -3 C -10 -8 -4 -12 0 -8 C 4 -12 10 -8 7 -3 Z"
                fill={t.accent}
              />
            </g>
          </svg>
        </div>

        {/* Two-column family list under the tree */}
        <div
          className="fotixa-tree-grid"
          style={{
            marginTop: 48,
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 56,
            textAlign: "left",
          }}
        >
          {FAMILY_SIDES.map((side) => (
            <div
              key={side.side}
              onMouseEnter={onHoverBranch}
              style={{
                padding: 24,
                background: t.bg,
                border: `1px solid ${t.accent}55`,
                position: "relative",
              }}
            >
              <div
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.32em",
                  color: t.accent,
                  marginBottom: 16,
                }}
              >
                {tt(side.titleKey).toUpperCase()}
              </div>
              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}
              >
                {[0, 1, 2].map((i) => (
                  <li
                    key={i}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                      paddingBottom: 8,
                      borderBottom: `1px dashed ${t.ink}22`,
                    }}
                  >
                    <span
                      style={{
                        fontFamily: "var(--font-ui)",
                        fontSize: 11,
                        fontWeight: 600,
                        letterSpacing: "0.18em",
                        color: `${t.ink}99`,
                      }}
                    >
                      {tt(`${side.memberPrefix}.${i}.role`).toUpperCase()}
                    </span>
                    <span
                      style={{
                        fontFamily: t.font || "var(--font-display)",
                        fontSize: 22,
                        color: t.ink,
                      }}
                    >
                      {tt(`${side.memberPrefix}.${i}.name`)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
